/**
 * Typed client for the open CEO Input Survey API.
 *
 * No auth — the survey is a shared link. Endpoints:
 *   - POST /api/survey/start    { identity } → { responseId }
 *   - POST /api/survey/answer   { responseId, ...answer } → { followup }
 *   - POST /api/survey/finalize { responseId, initiatives } → { mdUrl }
 *
 * Every call degrades gracefully: a network or server failure never blocks the
 * respondent from advancing. We surface { ok: false } and let the UI move on.
 */

const apiBase = (): string => import.meta.env.VITE_API_BASE_URL || "";

// --- Anonymous access gate ---
const ACCESS_KEY = "survey_access_token";
export function getAccessToken(): string | null {
  try {
    return sessionStorage.getItem(ACCESS_KEY);
  } catch {
    return null;
  }
}
export function setAccessToken(t: string): void {
  try {
    sessionStorage.setItem(ACCESS_KEY, t);
  } catch {
    /* ignore */
  }
}
/** Header to attach the access token (empty when no gate / no token). */
export function accessHeaders(): Record<string, string> {
  const t = getAccessToken();
  return t ? { "x-survey-access": t } : {};
}

/** Is the passcode gate turned on for this deployment? */
export async function gateConfig(): Promise<{ required: boolean }> {
  try {
    const r = await fetch(`${apiBase()}/api/survey/gate`);
    if (!r.ok) return { required: false };
    return (await r.json()) as { required: boolean };
  } catch {
    return { required: false };
  }
}

/** Submit the shared code; on success the token is stored for the session. */
export async function gateAccess(code: string): Promise<{ ok: boolean }> {
  try {
    const r = await fetch(`${apiBase()}/api/survey/gate`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ code }),
    });
    if (!r.ok) return { ok: false };
    const b = (await r.json()) as { ok?: boolean; token?: string };
    if (b.token) setAccessToken(b.token);
    return { ok: !!b.ok };
  } catch {
    return { ok: false };
  }
}

// No name collected — the survey is anonymous.
export type StartIdentity = {
  respondentLocation?: string;
  respondentDept?: string;
  roleBand?: string;
};

export async function startSurvey(identity: StartIdentity): Promise<{ ok: boolean; responseId?: string }> {
  try {
    const res = await fetch(`${apiBase()}/api/survey/start`, {
      method: "POST",
      headers: { "content-type": "application/json", ...accessHeaders() },
      body: JSON.stringify(identity),
    });
    if (!res.ok) return { ok: false };
    const body = (await res.json()) as { responseId: string };
    return { ok: true, responseId: body.responseId };
  } catch {
    return { ok: false };
  }
}

export type ThreadTurn = { question: string; answer: string };

export async function saveAnswer(params: {
  responseId: string;
  questionKey: string;
  questionText: string;
  answerText: string;
  orderIndex: number;
}): Promise<{ ok: boolean }> {
  try {
    const res = await fetch(`${apiBase()}/api/survey/answer`, {
      method: "POST",
      headers: { "content-type": "application/json", ...accessHeaders() },
      body: JSON.stringify(params),
    });
    return { ok: res.ok };
  } catch {
    return { ok: false };
  }
}

/** Speech-to-text: send recorded audio, get the transcript to drop in the box. */
export async function transcribeSurveyAudio(blob: Blob): Promise<{ ok: boolean; text?: string }> {
  try {
    const res = await fetch(`${apiBase()}/api/survey/transcribe`, {
      method: "POST",
      headers: { "content-type": "audio/webm", ...accessHeaders() },
      body: blob,
    });
    if (!res.ok) return { ok: false };
    const body = (await res.json()) as { text?: string; budgetExceeded?: boolean };
    if (body.budgetExceeded) return { ok: false };
    return { ok: true, text: body.text ?? "" };
  } catch {
    return { ok: false };
  }
}

/** The interview director: next tailored question, or done. */
export async function nextQuestion(params: {
  responseId: string;
  role?: string;
  involvedLabels?: string[];
  awareLabels?: string[];
  discussLabels?: string[];
  thread: ThreadTurn[];
  mode?: "survey" | "more";
}): Promise<{ question: string | null; done: boolean; budgetExceeded?: boolean; degraded?: boolean }> {
  try {
    const res = await fetch(`${apiBase()}/api/survey/next`, {
      method: "POST",
      headers: { "content-type": "application/json", ...accessHeaders() },
      body: JSON.stringify(params),
    });
    if (!res.ok) return { question: null, done: false, degraded: true };
    const body = (await res.json()) as {
      question: string | null;
      done: boolean;
      budgetExceeded?: boolean;
      degraded?: boolean;
    };
    return { question: body.question ?? null, done: body.done, budgetExceeded: body.budgetExceeded, degraded: body.degraded };
  } catch {
    return { question: null, done: false, degraded: true };
  }
}

export type InitiativeSubmission = {
  initiativeKey: string;
  label: string;
  isCustom: boolean;
  involvement: string | null;
  medal: string | null;
  followupText: string | null;
  orderIndex: number;
};

export async function finalizeSurvey(params: {
  responseId: string;
  initiatives: InitiativeSubmission[];
}): Promise<{ ok: boolean; mdUrl?: string }> {
  try {
    const res = await fetch(`${apiBase()}/api/survey/finalize`, {
      method: "POST",
      headers: { "content-type": "application/json", ...accessHeaders() },
      body: JSON.stringify(params),
    });
    if (!res.ok) return { ok: false };
    const body = (await res.json()) as { mdUrl?: string };
    return { ok: true, mdUrl: body.mdUrl };
  } catch {
    return { ok: false };
  }
}
